import { useMemo, useState, Fragment } from 'react';
import { push } from 'react-router-redux';
import { connect } from 'react-redux';
import QuickMenu from 'common/components/QuickMenu/QuickMenu';
import Part11Indicator from 'common/components/Part11Indicator/Part11Indicator';
import DialogDeactivateUser from 'common/components/DialogDeactivateUser/DialogDeactivateUser';

import {
    getTotalUsersDelegations,
    resetUserPassword,
    resendRegistrationEmail,
    setUserActiveV2,
    setUserActive,
    changeUserActive,
    getUsersTaskCount
} from 'part11/actions/users';
import styles from './styles.scss';
import DialogEndDelegations from 'common/components/DialogEndDelegations';

function QuickActionLabel({ labelText }) {
    return (
        <div className={styles.quickActionLabel} data-cy="UserInfo-UserQuickActions">
            <span data-cy="UserInfo-labelText">{labelText}</span>
            <Part11Indicator className={styles.part11Icon} />
        </div>
    );
}

function UserQuickActionsV2({ user, institutionId, push, resendRegistrationEmail, resetUserPassword, ...props }) {
    const [isEndDelegationDialogOpen, setIsEndDelegationDialogOpen] = useState(false);
    const [isTaskWarningDialogOpen, setTaskWarningDialogOpen] = useState(false);
    const [userTaskCount, setUserTaskCount] = useState(0);
    
    const editPath = useMemo(() => {
        const editPath = `/admin/users/${user.id}/edit`;
        return editPath;
    }, [user]);

    async function getUsersDelegations() {
        const { count } = await getTotalUsersDelegations(institutionId, user.id);
        
        return (count > 0);
    }

    function handleResenRegistrationEmail() {
        resendRegistrationEmail(user);
    }

    function handleEditUser() {
        push(editPath);
    }

    async function handleChangeUserActive() {
        const activeTasks = await props.getUsersTaskCount(user);
        
        if(activeTasks && user.isActive) {
            setUserTaskCount(activeTasks);
            setTaskWarningDialogOpen(true);
            return;
        }

        const hasDelegations = await getUsersDelegations();

        if(!activeTasks && hasDelegations && user.isActive) {
            return toggleEndDelegationDialog();
        } else if(activeTasks && !hasDelegations){
            return props.changeUserActive(user, institutionId);
        }
        return props.setUserActive(user, institutionId);
    }

    function handleSetUserActive() {
        const hasDelegations = getUsersDelegations();

        if(hasDelegations) {
            setIsEndDelegationDialogOpen(true);
        } else {
            props.setUserActive(user, institutionId);
        }

        setTaskWarningDialogOpen(false);
    }

    function confirmEndDelegationModal(value) {
        toggleEndDelegationDialog();
        props.setUserActiveV2(user, institutionId, value);
    }

    function handleResetPassword() {
        resetUserPassword(user);
    }

    function toggleEndDelegationDialog() {
        setIsEndDelegationDialogOpen(!isEndDelegationDialogOpen);
    }

    const options = useMemo(() => {
        return user.isSso ?
            [{
                label: 'Edit',
                handler: handleEditUser
            }, {
                label: <QuickActionLabel labelText={user.isActive ? 'Deactivate' : 'Activate'} />,
                handler: handleChangeUserActive
            }, {
                label: 'Account Reset',
                handler: handleResenRegistrationEmail
            }]
            :
            [{
                label: 'Edit',
                handler: handleEditUser
            }, {
                label: <QuickActionLabel labelText="Reset Password" />,
                handler: handleResetPassword
            }, {
                label: <QuickActionLabel labelText={user.isActive ? 'Deactivate' : 'Activate'} />,
                handler: handleChangeUserActive
            }, {
                label: 'Account Reset',
                handler: handleResenRegistrationEmail
            }];
    }, [user]);

    return (
        <Fragment>
            <QuickMenu headerText="Quick User Actions" theme="primary" options={options} />
            <DialogDeactivateUser
                isOpen={isTaskWarningDialogOpen}
                handleConfirm={handleSetUserActive}
                taskCount={userTaskCount}
                handleOnClose={() => setTaskWarningDialogOpen(false)}
            />
            <DialogEndDelegations
                user={user}
                isOpen={isEndDelegationDialogOpen}
                toggleDialog={toggleEndDelegationDialog}
                handleEndDelegations={confirmEndDelegationModal}
            />
        </Fragment>
    );
}

export default connect(null, {
    push,
    resetUserPassword,
    resendRegistrationEmail,
    setUserActiveV2,
    setUserActive,
    changeUserActive,
    getUsersTaskCount
})(UserQuickActionsV2);